import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { analytics } from '@/lib/analytics';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/contexts/OrganizationContext';

const SESSION_STORAGE_KEY = 'lp_live_session_id';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = (crypto && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Hook to track page views in Google Tag Manager
 * ONLY tracks on signup and onboarding pages to prevent tracking on other public pages
 *
 * Also pings the server's live-analytics endpoint on every navigation so
 * the admin Live Analytics page shows real session/page-view data.
 */
export function usePageTracking() {
  const [location] = useLocation();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  // Server-side live-analytics tracking — fire-and-forget on every route change.
  // Strip query strings: paths can include OAuth codes, password-reset tokens,
  // invite links, etc. Attach the JWT so the server can resolve the user;
  // userId is NOT sent in the body (would be spoofable on a public endpoint).
  useEffect(() => {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return;
    // ⚠ The one-time links carry their credential in the PATH, not the query:
    // /intake/<token> (activation form) and /kyc/<token> (document upload).
    // Stored as-is, our own analytics table would hold live links that the
    // intake/KYC tables deliberately keep only as hashes.
    const path = location.split('?')[0].replace(/^\/(intake|kyc)\/[^/]+/, '/$1/:token');
    const token = (typeof window !== 'undefined')
      ? (localStorage.getItem('token') || localStorage.getItem('adminToken'))
      : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    fetch('/api/analytics/track', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ path, sessionId }),
      keepalive: true,
    }).catch(() => { /* swallow — analytics failures must not break navigation */ });
  }, [location]);

  useEffect(() => {
    // CRITICAL: Only track on signup and onboarding pages
    const isSignupPage = location === '/auth/register';
    const isOnboardingPage = location.startsWith('/onboarding');

    if (!isSignupPage && !isOnboardingPage) {
      return; // Skip tracking on all other pages
    }

    // Track page view in Google Tag Manager
    try {
      // Determine page category based on route
      let pageCategory = 'other';
      let pageName = location;

      if (location.startsWith('/dashboard')) {
        pageCategory = 'dashboard';
        pageName = location.includes('personal') ? 'Personal Dashboard' : 
                   location.includes('company') ? 'Company Dashboard' : 'Dashboard';
      } else if (location.startsWith('/inbox')) {
        pageCategory = 'messaging';
        pageName = 'Inbox';
      } else if (location.startsWith('/contacts')) {
        pageCategory = 'crm';
        pageName = 'Contacts';
      } else if (location.startsWith('/campaigns')) {
        pageCategory = 'marketing';
        pageName = 'Campaigns';
      } else if (location.startsWith('/voice') || location.startsWith('/calls')) {
        pageCategory = 'voice';
        pageName = 'Voice & Calls';
      } else if (location.startsWith('/settings')) {
        pageCategory = 'settings';
        pageName = 'Settings';
      } else if (location.startsWith('/admin')) {
        pageCategory = 'admin';
        pageName = 'Admin Panel';
      } else if (location.startsWith('/auth')) {
        pageCategory = 'auth';
        pageName = 'Authentication';
      } else if (location === '/') {
        pageCategory = 'landing';
        pageName = 'Home';
      }

      // Track with user context if available
      analytics.trackPageView(
        pageName,
        pageCategory,
        user?.id,
        currentOrganization?.id,
        {
          path: location,
          url: window.location.href,
          referrer: document.referrer,
          userAgent: navigator.userAgent,
        }
      );
    } catch (error) {
      console.error('[GTM] Error tracking page:', error);
    }
  }, [location, user?.id, currentOrganization?.id]);
}
